import { FLOW_NODE_TYPES } from '../types';
import type { BotAction, FlowDefinition, FlowNode, FlowNodeType } from '../types';

const FIELD = 'mt-1 w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body';

/** What each type is for, in the words somebody building a flow would use. */
const EXPLAINS: Record<FlowNodeType, string> = {
  message: 'Says something and moves on.',
  list: 'A menu of up to ten options.',
  buttons: 'Up to three buttons.',
  capture: 'Asks a question and stores the answer.',
  condition: 'Branches on a value collected earlier.',
  action: 'Calls one of the saved actions.',
  set: 'Sets a value without asking.',
  dynamic_list: 'Fetches a list from an action and offers it as a menu.',
  handoff: 'Hands the conversation to a person.',
  end: 'Says goodbye and stops.',
};

/**
 * One step, as a form.
 *
 * Every "goes to" is a dropdown of steps that exist, which is the whole reason this
 * is a form rather than a text box: a target typed by hand and misspelt does not fail
 * loudly — the flow simply stops, and the customer is left looking at a conversation
 * that went quiet.
 */
export function NodeEditor({ id, node, definition, actions, onChange, onRename, onRemove }: {
  id: string;
  node: FlowNode;
  definition: FlowDefinition;
  actions: BotAction[];
  onChange: (patch: FlowNode) => void;
  onRename: (next: string) => void;
  onRemove: () => void;
}) {
  const ids = Object.keys(definition.nodes ?? {});
  const type = node.type ?? 'message';

  const set = (key: string, value: unknown) => onChange({ ...node, [key]: value });

  const Target = ({ label, field }: { label: string; field: string }) => (
    <label className="block">
      <span className="text-small font-medium text-(--color-muted)">{label}</span>
      <select
        value={(node[field] as string) ?? ''}
        onChange={(e) => set(field, e.target.value || null)}
        className={FIELD}
      >
        <option value="">Stop here</option>
        {ids.filter((n) => n !== id).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    </label>
  );

  return (
    <div className="rounded-xl border border-(--color-line) bg-(--color-surface) p-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={id}
          onChange={(e) => onRename(e.target.value)}
          aria-label="Step name"
          className={`${FIELD} max-w-52 font-medium`}
        />
        <select
          value={type}
          onChange={(e) => set('type', e.target.value)}
          className={`${FIELD} max-w-44`}
        >
          {FLOW_NODE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto rounded-lg border border-(--color-line) px-3 py-1.5 text-small text-(--color-muted) transition hover:text-(--color-bad)"
        >
          Remove
        </button>
      </div>

      <p className="mt-2 text-small text-(--color-muted)">{EXPLAINS[type]}</p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {(type === 'message' || type === 'end' || type === 'handoff' || type === 'list' || type === 'buttons' || type === 'dynamic_list') && (
          <label className="block sm:col-span-2">
            <span className="text-small font-medium text-(--color-muted)">
              What it says {type === 'handoff' || type === 'end' ? '(optional)' : ''}
            </span>
            <textarea
              value={(node.body as string) ?? ''}
              onChange={(e) => set('body', e.target.value)}
              rows={rows(node.body)}
              className={FIELD}
              placeholder="Hello {{company}}"
            />
          </label>
        )}

        {type === 'capture' && (
          <>
            <label className="block sm:col-span-2">
              <span className="text-small font-medium text-(--color-muted)">The question</span>
              <textarea
                value={(node.prompt as string) ?? ''}
                onChange={(e) => set('prompt', e.target.value)}
                rows={rows(node.prompt)}
                className={FIELD}
              />
            </label>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Store the answer as</span>
              <input value={(node.var as string) ?? ''} onChange={(e) => set('var', e.target.value)} className={FIELD} placeholder="mobile" />
            </label>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Check it is a</span>
              <select
                value={((node.validation as Record<string, unknown>)?.type as string) ?? 'nonempty'}
                onChange={(e) => set('validation', { ...(node.validation as object ?? {}), type: e.target.value })}
                className={FIELD}
              >
                {['nonempty', 'mobile', 'email', 'number', 'date', 'regex', 'length'].map((v) =>
                  <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-small font-medium text-(--color-muted)">What to say when it is wrong</span>
              <input
                value={((node.validation as Record<string, unknown>)?.error as string) ?? ''}
                onChange={(e) => set('validation', { ...(node.validation as object ?? {}), error: e.target.value })}
                className={FIELD}
                placeholder="Please enter a valid 10-digit mobile number."
              />
            </label>
          </>
        )}

        {type === 'condition' && (
          <label className="block">
            <span className="text-small font-medium text-(--color-muted)">Branch on</span>
            <input value={(node.var as string) ?? ''} onChange={(e) => set('var', e.target.value)} className={FIELD} placeholder="order_status" />
          </label>
        )}

        {(type === 'action' || type === 'dynamic_list') && (
          <>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Which action</span>
              <select
                value={(node.action as string) ?? ''}
                onChange={(e) => set('action', e.target.value)}
                className={FIELD}
              >
                <option value="">Choose…</option>
                {actions.map((a) => <option key={a.actionKey} value={a.actionKey}>{a.actionKey}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-small font-medium text-(--color-muted)">Method</span>
              <select value={(node.method as string) ?? 'POST'} onChange={(e) => set('method', e.target.value)} className={FIELD}>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            {type === 'action' && (
              <label className="block">
                <span className="text-small font-medium text-(--color-muted)">Wait for the answer?</span>
                <select value={(node.mode as string) ?? 'pull'} onChange={(e) => set('mode', e.target.value)} className={FIELD}>
                  <option value="pull">Yes — the flow uses it</option>
                  <option value="push">No — send and carry on</option>
                </select>
              </label>
            )}
            <div className="sm:col-span-2">
              <PairEditor
                label="Read from the answer"
                hint="Each one stores part of the response in a variable the rest of the flow can use."
                keyLabel="Variable"
                valueLabel="Where it is in the response"
                pairs={(node.response_map as Record<string, unknown>) ?? {}}
                onChange={(next) => set('response_map', next)}
                placeholderKey="customer_id"
                placeholderValue="data.CustomerID"
              />
            </div>
            {type === 'dynamic_list' && (
              <>
                <label className="block">
                  <span className="text-small font-medium text-(--color-muted)">Where the list is in the response</span>
                  <input value={(node.items_path as string) ?? ''} onChange={(e) => set('items_path', e.target.value)} className={FIELD} placeholder="data.Products" />
                </label>
                <label className="block">
                  <span className="text-small font-medium text-(--color-muted)">Store the choice as</span>
                  <input value={(node.var as string) ?? ''} onChange={(e) => set('var', e.target.value)} className={FIELD} placeholder="product" />
                </label>
                <Target label="If the list is empty, go to" field="empty_next" />
              </>
            )}
          </>
        )}

        {type === 'set' && (
          <div className="sm:col-span-2">
            <PairEditor
              label="Values to set"
              hint="A value may contain {{another_variable}}."
              keyLabel="Variable"
              valueLabel="Value"
              pairs={(node.set as Record<string, unknown>) ?? {}}
              onChange={(next) => set('set', next)}
              placeholderKey="query_type"
              placeholderValue="Complaint"
            />
          </div>
        )}

        {type !== 'condition' && type !== 'list' && type !== 'buttons' && type !== 'handoff' && (
          <Target label="Then go to" field="next" />
        )}

        {type === 'condition' && <Target label="If nothing matches, go to" field="default" />}
      </div>

      {(type === 'list' || type === 'buttons') && (
        <OptionsEditor
          type={type}
          node={node}
          ids={ids.filter((n) => n !== id)}
          onChange={onChange}
        />
      )}

      {type === 'condition' && (
        <CasesEditor node={node} ids={ids.filter((n) => n !== id)} onChange={onChange} />
      )}
    </div>
  );
}

/** Enough rows to show what is in the box. A message clipped mid-sentence in the
 *  editor is the one thing a builder must not do — that text is the product. */
function rows(value: unknown) {
  const text = typeof value === 'string' ? value : '';
  return Math.min(14, Math.max(3, text.split('\n').length + Math.floor(text.length / 90)));
}

const SMALL_FIELD = 'w-full rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body';

/**
 * The options on a menu, and where each one leads.
 *
 * These used to say "edited in the JSON view", which is a builder admitting it is not
 * one: a menu is the single most common thing anybody edits, and sending them to hand-
 * write a `transitions` map beside a `rows` array — two structures that have to agree
 * on every id — is how dangling targets get made. Here one row is one option, adding
 * one writes both structures, and removing one removes both.
 */
function OptionsEditor({ type, node, ids, onChange }: {
  type: 'list' | 'buttons';
  node: FlowNode;
  ids: string[];
  onChange: (patch: FlowNode) => void;
}) {
  type Option = { id: string; title: string; description?: string };
  const rowsIn: Option[] = (node.rows as Option[] | undefined) ?? (node.buttons as Option[] | undefined) ?? [];
  const transitions = (node.transitions as Record<string, string>) ?? {};
  const field = type === 'list' ? 'rows' : 'buttons';
  const max = type === 'list' ? 10 : 3;
  const titleMax = type === 'list' ? 24 : 20;

  const write = (next: typeof rowsIn, nextTransitions: Record<string, string>) =>
    onChange({ ...node, [field]: next, transitions: nextTransitions });

  const patch = (i: number, key: string, value: string) => {
    const next = rowsIn.map((r, n) => (n === i ? { ...r, [key]: value } : r));
    if (key !== 'id') return write(next, transitions);

    // Renaming an option carries its destination with it, or the row silently stops
    // leading anywhere.
    const { [rowsIn[i]?.id ?? '']: target, ...rest } = transitions;
    write(next, target === undefined ? rest : { ...rest, [value]: target });
  };

  const remove = (i: number) => {
    const { [rowsIn[i]?.id ?? '']: _gone, ...rest } = transitions;
    write(rowsIn.filter((_, n) => n !== i), rest);
  };

  const add = () => {
    let id = 'option';
    let n = 1;
    while (rowsIn.some((r) => r.id === id)) id = `option_${++n}`;
    write([...rowsIn, { id, title: '' }], transitions);
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-small font-medium text-(--color-muted)">
          {type === 'list' ? 'Menu options' : 'Buttons'}
        </span>
        <span className="text-small text-(--color-muted)">
          WhatsApp allows {max}; titles are cut at {titleMax} characters.
        </span>
      </div>

      {rowsIn.length === 0 && (
        <p className="mt-2 text-small text-(--color-muted)">
          None yet — a menu with no options shows the customer nothing.
        </p>
      )}

      <div className="mt-2 flex flex-col gap-2">
        {rowsIn.map((row, i) => (
          <div key={i} className="grid gap-2 rounded-lg border border-(--color-line-dim) p-3 sm:grid-cols-[10rem_1fr_1fr_auto]">
            <label className="block">
              <span className="text-small text-(--color-muted)">Value sent back</span>
              <input value={row.id ?? ''} onChange={(e) => patch(i, 'id', e.target.value)} className={SMALL_FIELD} />
            </label>
            <label className="block">
              <span className="text-small text-(--color-muted)">
                What the customer sees
                {(row.title?.length ?? 0) > titleMax && (
                  <span className="ml-1 text-(--color-warn)">· cut at {titleMax}</span>
                )}
              </span>
              <input value={row.title ?? ''} onChange={(e) => patch(i, 'title', e.target.value)} className={SMALL_FIELD} />
            </label>
            <label className="block">
              <span className="text-small text-(--color-muted)">
                {type === 'list' ? 'Goes to' : 'Goes to'}
              </span>
              <select
                value={transitions[row.id] ?? ''}
                onChange={(e) => write(rowsIn, e.target.value ? { ...transitions, [row.id]: e.target.value } : (() => { const { [row.id]: _g, ...rest } = transitions; return rest; })())}
                className={SMALL_FIELD}
              >
                <option value="">Nothing happens</option>
                {ids.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={() => remove(i)}
              className="self-end rounded-lg border border-(--color-line) px-3 py-2 text-small text-(--color-muted)"
            >
              Remove
            </button>

            {type === 'list' && (
              <label className="block sm:col-span-4">
                <span className="text-small text-(--color-muted)">Second line (optional)</span>
                <input value={row.description ?? ''} onChange={(e) => patch(i, 'description', e.target.value)} className={SMALL_FIELD} />
              </label>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={rowsIn.length >= max}
        className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body disabled:opacity-40"
      >
        Add an option
      </button>
    </div>
  );
}

/** The branches of a condition: a value, and the step it leads to. */
function CasesEditor({ node, ids, onChange }: {
  node: FlowNode;
  ids: string[];
  onChange: (patch: FlowNode) => void;
}) {
  const cases = (node.cases as Record<string, string>) ?? {};
  const entries = Object.entries(cases);

  const write = (next: [string, string][]) =>
    onChange({ ...node, cases: Object.fromEntries(next.filter(([k]) => k !== '')) });

  return (
    <div className="mt-4">
      <span className="text-small font-medium text-(--color-muted)">
        When <span className="font-mono">{(node.var as string) || 'the value'}</span> is…
      </span>

      <div className="mt-2 flex flex-col gap-2">
        {entries.map(([value, target], i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              value={value}
              onChange={(e) => write(entries.map((p, n) => (n === i ? [e.target.value, p[1]] : p)))}
              placeholder="true"
              className={SMALL_FIELD}
            />
            <select
              value={target}
              onChange={(e) => write(entries.map((p, n) => (n === i ? [p[0], e.target.value] : p)))}
              className={SMALL_FIELD}
            >
              <option value="">Nothing happens</option>
              {ids.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button
              type="button"
              onClick={() => write(entries.filter((_, n) => n !== i))}
              className="rounded-lg border border-(--color-line) px-3 py-2 text-small text-(--color-muted)"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => write([...entries, ['', '']])}
        className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body"
      >
        Add a case
      </button>
    </div>
  );
}

/** A map of name to value, as rows. Used for `set` and for reading an API answer. */
function PairEditor({ label, hint, keyLabel, valueLabel, pairs, onChange, placeholderKey, placeholderValue }: {
  label: string;
  hint: string;
  keyLabel: string;
  valueLabel: string;
  pairs: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  placeholderKey: string;
  placeholderValue: string;
}) {
  const entries = Object.entries(pairs).map(([k, v]) => [k, String(v ?? '')] as [string, string]);
  const write = (next: [string, string][]) =>
    onChange(Object.fromEntries(next.filter(([k]) => k !== '')));

  return (
    <div>
      <span className="text-small font-medium text-(--color-muted)">{label}</span>
      <p className="text-small text-(--color-muted)">{hint}</p>

      <div className="mt-2 flex flex-col gap-2">
        {entries.map(([key, value], i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              value={key}
              onChange={(e) => write(entries.map((p, n) => (n === i ? [e.target.value, p[1]] : p)))}
              placeholder={placeholderKey}
              aria-label={keyLabel}
              className={SMALL_FIELD}
            />
            <input
              value={value}
              onChange={(e) => write(entries.map((p, n) => (n === i ? [p[0], e.target.value] : p)))}
              placeholder={placeholderValue}
              aria-label={valueLabel}
              className={`${SMALL_FIELD} font-mono`}
            />
            <button
              type="button"
              onClick={() => write(entries.filter((_, n) => n !== i))}
              className="rounded-lg border border-(--color-line) px-3 py-2 text-small text-(--color-muted)"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => write([...entries, ['', '']])}
        className="mt-2 rounded-lg border border-(--color-line) px-3.5 py-2 text-body"
      >
        Add one
      </button>
    </div>
  );
}
